import { NavLink } from "react-router-dom";

export interface NavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
}

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: "Overview",
    items: [
      { to: "/admin", label: "Dashboard", icon: "ri-dashboard-line", end: true },
      { to: "/admin/reports", label: "Reports", icon: "ri-bar-chart-box-line" },
    ],
  },
  {
    label: "Management",
    items: [
      { to: "/admin/properties", label: "Projects", icon: "ri-building-2-line" },
      { to: "/admin/leads", label: "Leads & CRM", icon: "ri-contacts-book-line" },
      { to: "/admin/deals", label: "Deals", icon: "ri-hand-coin-line" },
      { to: "/admin/reminders", label: "Reminders", icon: "ri-alarm-line" },
      { to: "/admin/transactions", label: "Transactions", icon: "ri-exchange-funds-line" },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/admin/content", label: "Content", icon: "ri-file-text-line" },
      { to: "/admin/pages", label: "Pages", icon: "ri-file-code-line" },
      { to: "/admin/media", label: "Media", icon: "ri-image-line" },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/admin/users", label: "Users", icon: "ri-team-line" },
      { to: "/admin/settings?tab=branding", label: "Branding", icon: "ri-palette-line" },
      { to: "/admin/settings", label: "Settings", icon: "ri-settings-3-line" },
      { to: "/admin/security", label: "Security", icon: "ri-shield-check-line" },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}

export default function Sidebar({ collapsed, onToggle, onNavigate }: SidebarProps) {
  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 flex flex-col bg-foreground-950 text-background-50 transition-all duration-300 ${
        collapsed ? "w-[72px]" : "w-[248px]"
      }`}
    >
      <div className="flex items-center justify-between h-16 px-4 border-b border-background-50/10">
        {!collapsed && (
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary-600 text-background-50">
              <i className="ri-home-4-line text-lg"></i>
            </span>
            <div className="leading-tight">
              <span className="block font-heading text-sm font-semibold text-background-50">
                Nomad Livings
              </span>
              <span className="block text-[10px] uppercase tracking-widest text-background-50/50">
                Admin Panel
              </span>
            </div>
          </div>
        )}
        {collapsed && (
          <span className="flex items-center justify-center w-full h-9 rounded-xl bg-primary-600 text-background-50">
            <i className="ri-home-4-line text-lg"></i>
          </span>
        )}
        <button
          onClick={onToggle}
          className={`flex items-center justify-center w-8 h-8 rounded-lg text-background-50/60 hover:text-background-50 hover:bg-background-50/10 ${collapsed ? "hidden" : ""}`}
          aria-label="Collapse sidebar"
        >
          <i className="ri-menu-fold-line"></i>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            {!collapsed && (
              <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-widest text-background-50/40">
                {group.label}
              </p>
            )}
            <div className="space-y-1">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary-600 text-background-50"
                        : "text-background-50/70 hover:bg-background-50/10 hover:text-background-50"
                    }`
                  }
                  title={collapsed ? item.label : undefined}
                >
                  <span className="flex items-center justify-center w-5 h-5 shrink-0">
                    <i className={item.icon}></i>
                  </span>
                  {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-background-50/10">
        <a
          href="/"
          className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-background-50/70 hover:bg-background-50/10 hover:text-background-50 transition-colors ${
            collapsed ? "justify-center" : ""
          }`}
        >
          <span className="flex items-center justify-center w-5 h-5 shrink-0">
            <i className="ri-global-line"></i>
          </span>
          {!collapsed && <span className="whitespace-nowrap">View Website</span>}
        </a>
      </div>
    </aside>
  );
}